import { useState, useEffect, useCallback } from "react";
import { Route } from "react-router-dom";
import { toast } from "sonner";
import {
  Shield,
  Lock,
  RefreshCw,
  Settings,
  Globe,
  Wallet,
  CheckCircle2,
  Loader2,
  Download,
  Bell,
  Unlink,
  Activity,
  Send,
} from "lucide-react";
import { BACKEND_URL } from "../lib/config";
import { platformIcons } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useApi } from "../hooks/useApi";
import { DashboardLayout } from "../components/DashboardLayout";

export const SettingsPage = () => {
  const { user } = useAuth();
  const api = useApi();
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [platforms, setPlatforms] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    in_app_notifications: true,
  });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [accountsRes, platformsRes, txRes] = await Promise.all([
        api.get("/integrations/connected"),
        api.get("/integrations/platforms"),
        api.get("/blockchain/transactions"),
      ]);
      setConnectedAccounts(accountsRes.data);
      setPlatforms(platformsRes.data);
      setTransactions(txRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const connectAccount = async (platform) => {
    try {
      const res = await api.post("/integrations/connect", { platform });
      toast.success(`Connected to ${platforms[platform]?.name}! ${res.data.data_synced} data points synced.`);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to connect");
    }
  };

  const disconnectAccount = async (accountId) => {
    if (!window.confirm("Disconnect this account?")) return;
    try {
      await api.delete(`/integrations/${accountId}`);
      toast.success("Account disconnected");
      loadData();
    } catch (e) {
      toast.error("Failed to disconnect");
    }
  };

  const syncAccount = async (accountId) => {
    setSyncing(accountId);
    try {
      const res = await api.post(`/integrations/${accountId}/sync`);
      toast.success(`Synced ${res.data.new_data_points} new data points`);
      loadData();
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setSyncing(null);
    }
  };

  const updateNotificationSettings = async (key, value) => {
    const newSettings = { ...notificationSettings, [key]: value };
    setNotificationSettings(newSettings);
    try {
      await api.put("/notifications/settings", newSettings);
      toast.success("Settings updated");
    } catch (e) {
      toast.error("Failed to update settings");
    }
  };

  const getPlatformIcon = (platform) => {
    const Icon = platformIcons[platform] || Globe;
    return Icon;
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="settings-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Settings</h1>
          <p className="text-gray-400 mt-1">Manage your account and connected services</p>
        </div>

        {/* Profile */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-purple-500/20">
          <h2 className="text-lg font-semibold text-white mb-4">Profile</h2>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white text-2xl font-bold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-xl font-semibold text-white">{user?.name}</p>
              <p className="text-gray-400">{user?.email}</p>
              <p className="text-sm text-gray-500 mt-1">
                Member since {new Date(user?.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Connected Accounts */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-purple-500/20">
          <h2 className="text-lg font-semibold text-white mb-4">Connected Accounts</h2>
          <p className="text-sm text-gray-400 mb-4">Connect external services to automatically import your data (simulated)</p>
          
          {/* Connected */}
          {connectedAccounts.filter(a => a.status === 'connected').length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-400 mb-3">Connected</h3>
              <div className="space-y-2">
                {connectedAccounts.filter(a => a.status === 'connected').map((account) => {
                  const Icon = getPlatformIcon(account.platform);
                  return (
                    <div key={account.id} className="flex items-center justify-between p-4 bg-green-500/10 rounded-xl border border-green-500/20">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{account.display_name}</p>
                          <p className="text-sm text-gray-400">
                            {account.data_synced} data points • Last sync: {account.last_sync ? new Date(account.last_sync).toLocaleDateString() : 'Never'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => syncAccount(account.id)}
                          disabled={syncing === account.id}
                          className="p-2 text-cyan-400 hover:bg-cyan-500/20 rounded-lg transition"
                        >
                          {syncing === account.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => disconnectAccount(account.id)}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition"
                        >
                          <Unlink className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Available to Connect */}
          <div>
            <h3 className="text-sm font-medium text-gray-400 mb-3">Available Services</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(platforms).map(([key, platform]) => {
                const isConnected = connectedAccounts.some(a => a.platform === key && a.status === 'connected');
                const Icon = getPlatformIcon(key);
                return (
                  <button
                    key={key}
                    onClick={() => !isConnected && connectAccount(key)}
                    disabled={isConnected}
                    className={`flex items-center gap-3 p-4 rounded-xl border transition ${
                      isConnected
                        ? 'bg-green-500/10 border-green-500/20 cursor-default'
                        : 'bg-white/5 border-purple-500/20 hover:bg-white/10 hover:border-purple-500/40'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${isConnected ? 'text-green-400' : 'text-gray-400'}`} />
                    <div className="text-left">
                      <p className="text-white text-sm font-medium">{platform.name}</p>
                      <p className="text-xs text-gray-500 capitalize">{platform.category}</p>
                    </div>
                    {isConnected && <CheckCircle2 className="w-4 h-4 text-green-400 ml-auto" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-purple-500/20">
          <h2 className="text-lg font-semibold text-white mb-4">Notification Settings</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-purple-500/10">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-purple-400" />
                <div>
                  <p className="font-medium text-white">In-App Notifications</p>
                  <p className="text-sm text-gray-400">Receive notifications within the app</p>
                </div>
              </div>
              <button
                onClick={() => updateNotificationSettings('in_app_notifications', !notificationSettings.in_app_notifications)}
                className={`w-12 h-6 rounded-full transition ${
                  notificationSettings.in_app_notifications ? 'bg-purple-600' : 'bg-gray-600'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                  notificationSettings.in_app_notifications ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-purple-500/10">
              <div className="flex items-center gap-3">
                <Send className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="font-medium text-white">Email Notifications</p>
                  <p className="text-sm text-gray-400">Receive email updates for important events</p>
                </div>
              </div>
              <button
                onClick={() => updateNotificationSettings('email_notifications', !notificationSettings.email_notifications)}
                className={`w-12 h-6 rounded-full transition ${
                  notificationSettings.email_notifications ? 'bg-cyan-600' : 'bg-gray-600'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                  notificationSettings.email_notifications ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
          </div>
        </div>

        {/* Privacy & Security */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-purple-500/20">
          <h2 className="text-lg font-semibold text-white mb-4">Privacy & Security</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-purple-500/10">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-green-400" />
                <div>
                  <p className="font-medium text-white">Data Encryption</p>
                  <p className="text-sm text-gray-400">AES-256 encryption enabled</p>
                </div>
              </div>
              <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
                Active
              </span>
            </div>
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-purple-500/10">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="font-medium text-white">GDPR Compliance</p>
                  <p className="text-sm text-gray-400">Your data rights are protected</p>
                </div>
              </div>
              <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm font-medium">
                Compliant
              </span>
            </div>
          </div>
        </div>

        {/* Blockchain Activity */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
          <h2 className="text-lg font-semibold text-white mb-4">Blockchain Activity</h2>
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 mb-4">
            <p className="text-sm text-yellow-400">
              <strong>Simulated Blockchain:</strong> This MVP uses simulated blockchain transactions.
            </p>
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8">
              <Wallet className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">No blockchain transactions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx) => (
                <div key={tx.id} className="p-4 bg-white/5 rounded-xl border border-purple-500/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      <span className="font-medium text-white capitalize">{tx.transaction_type}</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(tx.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-purple-400/60 mt-2 font-mono truncate">{tx.block_hash}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Export Data */}
        <div className="mt-6 bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
          <h2 className="text-lg font-semibold text-white mb-4">Export Your Data</h2>
          <p className="text-sm text-gray-400 mb-4">Download your data in various formats</p>
          <div className="flex flex-wrap gap-3">
            <a
              href={`${BACKEND_URL}/api/export/data.csv`}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-400 rounded-xl hover:bg-purple-500/30 transition"
            >
              <Download className="w-4 h-4" />
              Data Items (CSV)
            </a>
            <a
              href={`${BACKEND_URL}/api/export/summary.csv`}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-xl hover:bg-cyan-500/30 transition"
            >
              <Download className="w-4 h-4" />
              Portfolio Summary
            </a>
            <a
              href={`${BACKEND_URL}/api/export/transactions.csv`}
              className="flex items-center gap-2 px-4 py-2 bg-violet-500/20 text-violet-400 rounded-xl hover:bg-violet-500/30 transition"
            >
              <Download className="w-4 h-4" />
              Transactions
            </a>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// Protected Route
